import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
  ViewEncapsulation,
  OnInit,
  output,
} from '@angular/core';
import { faLocationDot, faRefresh, faSearch } from '@fortawesome/free-solid-svg-icons';
import {
  ButtonComponent,
  InputComponent,
  DialogHeaderComponent,
  FaIconComponent,
  MessageComponent,
} from '@akostm/orbit-ui';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { FormControl } from '@angular/forms';
import * as mapboxgl from 'mapbox-gl';
import { MapComponent } from 'ngx-mapbox-gl';
import { Subject, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocationValues } from '../../../interfaces';
import { NativePermissionsService, WireApiService } from '../../../services';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { logger } from '../../../utils';
import { faSend } from '@fortawesome/duotone-regular-svg-icons';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';

interface MapMoveEvent {
  originalEvent?: MouseEvent | WheelEvent | TouchEvent;
  eventSource?: string;
}

@Component({
  selector: 'shared-location-overlay',
  standalone: true,
  imports: [
    ButtonComponent,
    DialogHeaderComponent,
    InputComponent,
    FaIconComponent,
    MessageComponent,
    TranslatePipe,
    MapComponent,
  ],
  templateUrl: './location-overlay.component.html',
  styleUrl: './location-overlay.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationOverlayComponent implements OnInit {
  readonly rightAddonIcon = input<IconProp>(faSend);
  readonly #dialogRef = inject(DialogRef);
  readonly #wireApiService = inject(WireApiService);
  readonly #nativePermissions = inject(NativePermissionsService);
  readonly data: {
    location: GeolocationPosition;
  } = inject(DIALOG_DATA);
  readonly #translateService = inject(TranslateService);
  readonly #toastr = inject(ToastrService);
  readonly title = input<string>(
    this.#translateService.instant('SHARED.TEMPLATES.LOCATION_OVERLAY.TITLE')
  );
  readonly closeLabel = input<string>(
    this.#translateService.instant('SHARED.TEMPLATES.LOCATION_OVERLAY.CLOSE')
  );
  readonly userLocation = signal<{ latitude: number; longitude: number } | null>(null);
  readonly language = signal(this.#translateService.getCurrentLang());
  readonly locationDisabledError = signal<{
    code: number;
    message: string;
    link?: { url: string; text: string };
  } | null>(null);
  readonly webGlError = signal<boolean>(false);
  locationSource = signal<'GPS' | 'MANUAL'>('GPS');

  readonly searchIcon = faSearch;
  readonly locationDotIcon = faLocationDot;
  readonly retryIcon = faRefresh;
  readonly spinnerIcon = faSpinnerThird;
  readonly mapOptions = {
    accessToken:
      'pk.eyJ1Ijoib3BlbnJlc2VhcmNoIiwiYSI6ImNrcDU2cXhhczFyMHIycG13eGQ1enl6ajgifQ.yIruwhwKAxueP8JJUHYrKQ',
    latitude: 47.372617,
    longitude: 14.143702,
    zoom: 5,
  };

  readonly addressController = new FormControl<string | null>(null);

  readonly geolocate = new mapboxgl.GeolocateControl({
    positionOptions: {
      enableHighAccuracy: true,
    },
    trackUserLocation: false,
  });
  readonly mapCenterChanged = new Subject<mapboxgl.Map>();
  readonly sendLocation = output<LocationValues>();
  positionLoading = signal(true);
  messageSending = signal(false);
  map: mapboxgl.Map | undefined;
  #accuracy = 35;

  constructor() {
    this.mapCenterChanged.subscribe((map) => {
      map.on('dragend', () => this.locationSource.set('MANUAL'));
    });
    this.mapCenterChanged
      .pipe(
        tap(() => {
          this.positionLoading.set(true);
          this.addressController.disable();
        }),
        switchMap((event: mapboxgl.Map) => {
          return this.#wireApiService.geocodingApi.reverseGeocode({
            longitude: event.getCenter().lng,
            latitude: event.getCenter().lat,
            language: this.language(),
          });
        }),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (address) => {
          if (address?.formattedAddress) {
            this.addressController.setValue(address.formattedAddress);
            this.addressController.markAsUntouched();
            this.addressController.markAsPristine();
          } else {
            this.addressController.setValue('');
            this.addressController.markAsUntouched();
            this.addressController.markAsPristine();
          }
          this.#accuracy = 0;
          this.addressController.enable();
          this.positionLoading.set(false);
          // Trigger validation after map center changes
          this.addressController.updateValueAndValidity();
        },
        error: (err) => {
          logger.error(err);
          this.addressController.setValue('');
          this.addressController.enable();
          this.positionLoading.set(false);
          // Trigger validation even on error
          this.addressController.updateValueAndValidity();
        },
      });
  }

  ngOnInit(): void {
    // Check WebGL support before proceeding
    if (!this.#isWebGLSupported()) {
      this.webGlError.set(true);
      this.positionLoading.set(false);
      return;
    }

    // Use provided location if available, otherwise attempt to get user's current location
    // Geolocation is necessary here as this is a location picker component that needs
    // to initialize the map center either from provided data or user's current position
    if (this.data?.location) {
      this.userLocation.set({
        latitude: this.data.location.coords.latitude,
        longitude: this.data.location.coords.longitude,
      });
      this.mapOptions.latitude = this.data.location.coords.latitude;
      this.mapOptions.longitude = this.data.location.coords.longitude;
      this.mapOptions.zoom = 14;
    } else {
      // Fire-and-forget: ngOnInit is synchronous, so we kick off the async bridge flow
      // and explicitly drop the Promise with `void` to keep linting happy.
      void this.#initializeLocationPermission();
    }
  }

  /**
   * Attempts to get the user's current location via geolocation API.
   * This is necessary for the location picker component to initialize the map
   * at the user's current position when no location is provided in dialog data.
   */
  tryToGetLocation(): void {
    this.positionLoading.set(true);
    this.locationDisabledError.set(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.map?.jumpTo(
          {
            center: [position.coords.longitude, position.coords.latitude],
            zoom: 14,
          },
          { eventSource: 'positionGotFromBrowser' }
        );
      },
      (err) => {
        if (err.code === 1) {
          this.#setLocationDeniedError(err.code);
        }
        this.addressController.updateValueAndValidity();
        this.positionLoading.set(false);
        logger.error(err);
      }
    );
  }

  async #initializeLocationPermission(): Promise<void> {
    if (!navigator.geolocation) {
      this.positionLoading.set(false);
      return;
    }

    if (!this.#nativePermissions.isBridgeAvailable()) {
      this.tryToGetLocation();
      return;
    }

    this.positionLoading.set(true);

    try {
      const state = await this.#nativePermissions.checkPermission('geolocation');

      if (state === 'granted' || state === 'unsupported') {
        this.tryToGetLocation();
        return;
      }

      if (state === 'prompt') {
        const granted = await this.#nativePermissions.requestPermission('geolocation');
        if (granted) {
          this.tryToGetLocation();
          return;
        }
      }

      this.#setLocationDeniedError();
    } catch (error) {
      logger.error(error);
      this.tryToGetLocation();
    }
  }

  #setLocationDeniedError(code = 1): void {
    const helperUrl = this.locationEnableHelperLinkUrl();
    this.locationDisabledError.set({
      message: 'SHARED.TEMPLATES.LOCATION_OVERLAY.LOCATION_DISABLED_ERROR',
      code,
      ...(helperUrl && {
        link: {
          url: helperUrl,
          text: this.#translateService.instant(
            'SHARED.TEMPLATES.LOCATION_OVERLAY.HOW_TO_ENABLE_LOCATION'
          ),
        },
      }),
    });
    this.addressController.updateValueAndValidity();
    this.positionLoading.set(false);
  }

  mapCreate(map: mapboxgl.Map): void {
    this.map = map;
    this.map?.setLanguage(this.language());
    this.map?.addControl(this.geolocate);
    this.map.scrollZoom.disable();
    this.map.scrollZoom.enable({ around: 'center' });
  }

  mapMoved(event: MapMoveEvent): void {
    if (event.originalEvent instanceof WheelEvent) {
      return;
    }
    this.positionLoading.set(false);
    if (event.eventSource === 'positionGotFromBrowser') {
      setTimeout(() => this.geolocate.trigger(), 0);
    }
    if (this.map) {
      this.mapCenterChanged.next(this.map);
    }
  }

  async handleSendButton(): Promise<void> {
    this.messageSending.set(true);
    const center = this.map?.getCenter();

    if (this.addressController.dirty && this.addressController.touched) {
      this.#verifyLocation();
      this.messageSending.set(false);
      return;
    }

    if (center) {
      if (center.lat === this.mapOptions.latitude && center.lng === this.mapOptions.longitude) {
        this.#mapWasNotMoved();
        this.messageSending.set(false);
      } else {
        this.sendLocation.emit({
          latitude: center.lat,
          longitude: center.lng,
          formatted_address: this.addressController?.value || undefined,
          accuracy: this.#accuracy,
          source: this.locationSource(),
          language: this.language(),
        });
      }
    }
  }

  async getCoordinates(): Promise<void> {
    this.positionLoading.set(true);
    const address = this.addressController?.value;
    if (address) {
      try {
        const result = await this.#wireApiService.geocodingApi.searchGeocode({
          text: address,
          language: this.language(),
        });

        if (result?.formattedAddress && result?.location?.longitude && result?.location?.latitude) {
          this.addressController.setValue(result.formattedAddress);
          this.addressController.markAsUntouched();
          this.addressController.markAsPristine();

          this.map?.jumpTo({
            center: [result.location.longitude, result.location.latitude],
            zoom: 14,
          });
          this.#accuracy = 0;
        } else {
          this.#toastr.error(
            this.#translateService.instant(
              'SHARED.TEMPLATES.LOCATION_OVERLAY.LOCATION_SEARCH_ERROR'
            )
          );
          throw new Error('Location search failed');
        }
      } catch (err) {
        logger.error(err);
        throw err; // Re-throw so caller can handle if needed
      } finally {
        this.positionLoading.set(false);
      }
    } else {
      this.positionLoading.set(false);
    }
  }

  #mapWasNotMoved() {
    this.#toastr.info(
      this.#translateService.instant(
        'SHARED.TEMPLATES.LOCATION_OVERLAY.SET_LOCATION_ALERT.MESSAGE'
      ),
      this.#translateService.instant('SHARED.TEMPLATES.LOCATION_OVERLAY.SET_LOCATION_ALERT.TITLE')
    );
  }

  async #verifyLocation() {
    try {
      await this.getCoordinates();
      // Only show info toast if getCoordinates() succeeded (no error thrown)
      this.#toastr.info(
        this.#translateService.instant(
          'SHARED.TEMPLATES.LOCATION_OVERLAY.VERIFY_LOCATION_ALERT.MESSAGE'
        ),
        this.#translateService.instant(
          'SHARED.TEMPLATES.LOCATION_OVERLAY.VERIFY_LOCATION_ALERT.TITLE'
        )
      );
    } catch (err) {
      // Error toast already shown in getCoordinates(), so we don't show info toast
      logger.error('Location verification failed:', err);
    }
  }

  locationEnableHelperLinkUrl() {
    if (this.#isIOSSafari()) {
      return 'https://support.apple.com/en-us/102647';
    } else if (this.#isIOSChrome()) {
      return 'https://support.google.com/chrome/answer/142065?hl=en&co=GENIE.Platform%3DiOS';
    } else if (this.#isAndroid()) {
      return 'https://support.google.com/chrome/answer/142065?hl=en&co=GENIE.Platform%3DAndroid';
    } else if (this.#isChrome()) {
      return 'https://support.google.com/chrome/answer/142065?hl=en&co=GENIE.Platform%3DDesktop';
    } else if (this.#isSafari()) {
      return 'https://support.apple.com/guide/safari/websites-location-ibrw1074/mac';
    } else if (this.#isFirefox()) {
      return 'https://support.mozilla.org/en-US/kb/does-firefox-share-my-location-websites';
    } else if (this.#isEdge()) {
      return 'https://support.microsoft.com/en-us/microsoft-edge/location-and-privacy-in-microsoft-edge-31b5d154-0d7b-deaa-3629-72e3241fe6d7';
    }
    return null; // Default to Chrome help
  }

  #isChrome(): boolean {
    return /Chrome/.test(navigator.userAgent) && !/Edge/.test(navigator.userAgent);
  }

  #isSafari(): boolean {
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android');
  }

  #isFirefox(): boolean {
    return /Firefox/.test(navigator.userAgent);
  }

  #isEdge(): boolean {
    return /Edge/.test(navigator.userAgent);
  }

  #isIOSSafari(): boolean {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      !('MSStream' in window) &&
      /Safari/.test(navigator.userAgent) &&
      !/CriOS/.test(navigator.userAgent)
    );
  }

  #isIOSChrome(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && /CriOS/.test(navigator.userAgent);
  }

  #isAndroid(): boolean {
    return /Android/.test(navigator.userAgent);
  }

  #isWebGLSupported(): boolean {
    try {
      const canvas = document.createElement('canvas');
      const gl =
        canvas.getContext('webgl') ||
        canvas.getContext('experimental-webgl') ||
        canvas.getContext('webgl2');
      return !!gl;
    } catch {
      return false;
    }
  }

  getWebGLEnableHelpUrl(): string | null {
    if (this.#isIOSSafari()) {
      return 'https://support.apple.com/guide/safari/use-hardware-acceleration-ibrw1074/mac';
    } else if (this.#isIOSChrome()) {
      return 'https://support.google.com/chrome/answer/1220892';
    } else if (this.#isAndroid()) {
      return 'https://support.google.com/chrome/answer/1220892';
    } else if (this.#isFirefox()) {
      return 'https://support.mozilla.org/en-US/kb/upgrade-graphics-drivers-use-hardware-acceleration#w_webgl';
    } else if (this.#isChrome()) {
      return 'https://support.google.com/chrome/answer/1220892';
    } else if (this.#isSafari()) {
      return 'https://support.apple.com/guide/safari/use-hardware-acceleration-ibrw1074/mac';
    } else if (this.#isEdge()) {
      return 'https://learn.microsoft.com/en-us/microsoft-edge/devtools-guide-chromium/3d-view/';
    }
    return null;
  }

  closeDialog(): void {
    this.#dialogRef.close();
  }
}
